import React, { useState, useMemo } from 'react';
import Layout from '../components/Layout';
import { useBills } from '../contexts/BillContext';
import { useAccounts } from '../contexts/AccountContext';
import { useTransactions } from '../contexts/TransactionContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import {
  Plus,
  Calendar,
  List,
  MoreVertical,
  Edit2,
  Trash2,
  Check,
  Bell,
  AlertCircle,
  CreditCard,
  CheckCircle2,
  Clock,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format, differenceInDays } from 'date-fns';
import {
  calculateBillsCalendarSummary,
  getBillDueStatus,
  BILL_STATUS_META,
} from 'shared/utils/billsCalendarUtils';
import BillsCalendarView from '../components/BillsCalendarView';
import BillsDueRemindersBanner from '../components/BillsDueRemindersBanner';

const Bills = () => {
  const { bills, loading, addBill, updateBill, deleteBill, markBillPaid, getBillStatus } = useBills();
  const { accounts } = useAccounts();
  const { addTransaction } = useTransactions();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);

  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' | 'list'
  const [currentCalendarMonth, setCurrentCalendarMonth] = useState(new Date());
  const [showBillDialog, setShowBillDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [editingBill, setEditingBill] = useState(null);
  const [deletingBill, setDeletingBill] = useState(null);
  const [payingBill, setPayingBill] = useState(null);
  const [activeFilter, setActiveFilter] = useState('all');
  const [billForm, setBillForm] = useState({
    name: '',
    amount: '',
    dueDate: '',
    repeat: 'none',
    reminderDays: '3',
    account: ''
  });

  const calendarSummary = useMemo(() => {
    return calculateBillsCalendarSummary(bills, currentCalendarMonth);
  }, [bills, currentCalendarMonth]);

  const filteredBills = useMemo(() => {
    return bills.filter(bill => {
      const status = getBillStatus(bill);
      if (activeFilter === 'all') return true;
      if (activeFilter === 'upcoming') return status === 'upcoming' || status === 'due-today';
      if (activeFilter === 'paid') {
        // Consider any bill with a lastPaidDate as "paid" for the Paid tab,
        // even if it's a recurring bill whose next due date has been advanced.
        return status === 'paid' || !!bill.lastPaidDate;
      }
      return true;
    });
  }, [bills, activeFilter, getBillStatus]);

  const handleOpenAddDialog = (prefillDueDate = null) => {
    setEditingBill(null);
    setBillForm({
      name: '',
      amount: '',
      dueDate: typeof prefillDueDate === 'string' && prefillDueDate ? prefillDueDate : format(new Date(), 'yyyy-MM-dd'),
      repeat: 'monthly',
      reminderDays: '3',
      account: ''
    });
    setShowBillDialog(true);
  };

  const handleOpenEditDialog = (bill) => {
    setEditingBill(bill);
    setBillForm({
      name: bill.name,
      amount: bill.amount.toString(),
      dueDate: format(bill.dueDate, 'yyyy-MM-dd'),
      repeat: bill.repeat || 'none',
      reminderDays: bill.reminderDays?.toString() || '3',
      account: bill.account || ''
    });
    setShowBillDialog(true);
  };

  const handleSaveBill = async () => {
    if (!billForm.name.trim()) {
      toast.error('Please enter a bill name');
      return;
    }
    if (!billForm.amount || parseFloat(billForm.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    if (!billForm.dueDate) {
      toast.error('Please select a due date');
      return;
    }

    try {
      const billData = {
        name: billForm.name.trim(),
        amount: parseFloat(billForm.amount),
        dueDate: new Date(billForm.dueDate),
        repeat: billForm.repeat,
        reminderDays: parseInt(billForm.reminderDays) || 3,
        account: billForm.account === 'none' ? null : (billForm.account || null)
      };

      if (editingBill) {
        await updateBill(editingBill.id, billData);
        toast.success('Bill updated!');
      } else {
        await addBill(billData);
        toast.success('Bill added!');
      }

      setShowBillDialog(false);
      setEditingBill(null);
    } catch (error) {
      toast.error('Failed to save bill');
    }
  };

  const handleDeleteBill = async () => {
    if (!deletingBill) return;
    try {
      await deleteBill(deletingBill.id);
      toast.success('Bill deleted');
      setShowDeleteDialog(false);
      setDeletingBill(null);
    } catch (error) {
      toast.error('Failed to delete bill');
    }
  };

  const handleMarkPaid = async (createTransaction = false) => {
    if (!payingBill) return;
    try {
      await markBillPaid(payingBill.id);
      
      if (createTransaction && payingBill.account) {
        await addTransaction({
          type: 'expense',
          amount: payingBill.amount,
          category: 'Bills & Fees',
          categoryIcon: '💳',
          account: payingBill.account,
          date: new Date(),
          description: `Bill payment: ${payingBill.name}`
        });
        toast.success('Bill marked as paid and transaction logged!');
      } else {
        toast.success('Bill marked as paid!');
      }
      
      setShowPayDialog(false);
      setPayingBill(null);
    } catch (error) {
      toast.error('Failed to mark bill as paid');
    }
  };

  const getStatusBadge = (bill, overrideStatus) => {
    const status = overrideStatus || getBillStatus(bill);
    const styles = {
      'upcoming': { bg: isDark ? '#3A3A3C' : '#E9ECEF', color: theme.textSecondary, text: 'Upcoming' },
      'due-today': { bg: '#FEE2E2', color: '#DC2626', text: 'Due Today' },
      'overdue': { bg: '#7F1D1D', color: '#FFFFFF', text: 'Overdue' },
      'paid': { bg: '#D1FAE5', color: '#059669', text: 'Paid' }
    };
    return styles[status];
  };

  const getDaysUntilDue = (dueDate) => {
    const days = differenceInDays(dueDate, new Date());
    if (days === 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days < 0) return `${Math.abs(days)} days overdue`;
    return `${days} days`;
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Top Header & View Mode Switch */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight" style={{ color: theme.text }}>
              Bills & Due Dates
            </h1>
            <p className="text-sm mt-1" style={{ color: theme.textSecondary }}>
              Track recurring bills, monthly payment calendar, and urgent due-date reminders.
            </p>
          </div>

          <div className="flex items-center gap-3">
            {/* View Mode Toggle: Calendar vs List */}
            <div
              className="flex items-center p-1 rounded-xl border bg-black/5 dark:bg-white/5"
              style={{ borderColor: isDark ? '#3A3A3C' : '#E5E5EA' }}
            >
              <button
                onClick={() => setViewMode('calendar')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'calendar'
                    ? 'shadow-sm text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                }`}
                style={{
                  backgroundColor: viewMode === 'calendar' ? theme.accent : 'transparent',
                }}
              >
                <Calendar className="w-3.5 h-3.5" /> Calendar
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                  viewMode === 'list'
                    ? 'shadow-sm text-white'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
                }`}
                style={{
                  backgroundColor: viewMode === 'list' ? theme.accent : 'transparent',
                }}
              >
                <List className="w-3.5 h-3.5" /> List
              </button>
            </div>

            <Button
              onClick={() => handleOpenAddDialog()}
              className="flex items-center gap-2 rounded-xl h-10 px-4 font-semibold text-sm shadow-sm"
              style={{ backgroundColor: theme.accent, color: 'white' }}
              data-testid="add-bill-btn"
            >
              <Plus className="w-4 h-4" />
              Add Bill
            </Button>
          </div>
        </div>

        {/* High-Level Financial Metrics Row */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
          <div
            className="p-4 rounded-2xl border transition-all"
            style={{
              backgroundColor: theme.cardBackground,
              borderColor: isDark ? '#3A3A3C' : '#E5E5EA',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Total Monthly Bills
              </span>
              <div
                className="w-7 h-7 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: `${theme.accent}15` }}
              >
                <Calendar className="w-4 h-4" style={{ color: theme.accent }} />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold mt-2" style={{ color: theme.text }}>
              {formatCurrency(calendarSummary.totalBillsAmount)}
            </div>
            <span className="text-xs mt-1 block" style={{ color: theme.textSecondary }}>
              {bills.length} scheduled item{bills.length !== 1 ? 's' : ''}
            </span>
          </div>

          <div
            className="p-4 rounded-2xl border transition-all"
            style={{
              backgroundColor: theme.cardBackground,
              borderColor: isDark ? '#3A3A3C' : '#E5E5EA',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Remaining Due
              </span>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-orange-500/15">
                <Clock className="w-4 h-4 text-orange-500" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold mt-2 text-orange-600 dark:text-orange-400">
              {formatCurrency(calendarSummary.totalDue)}
            </div>
            <span className="text-xs mt-1 block" style={{ color: theme.textSecondary }}>
              {calendarSummary.unpaidCount} unpaid bill{calendarSummary.unpaidCount !== 1 ? 's' : ''}
            </span>
          </div>

          <div
            className="p-4 rounded-2xl border transition-all"
            style={{
              backgroundColor: theme.cardBackground,
              borderColor: isDark ? '#3A3A3C' : '#E5E5EA',
            }}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium" style={{ color: theme.textSecondary }}>
                Settled / Paid
              </span>
              <div className="w-7 h-7 rounded-lg flex items-center justify-center bg-emerald-500/15">
                <CheckCircle2 className="w-4 h-4 text-emerald-500" />
              </div>
            </div>
            <div className="text-xl md:text-2xl font-bold mt-2 text-emerald-600 dark:text-emerald-400">
              {formatCurrency(calendarSummary.totalPaid)}
            </div>
            <span className="text-xs mt-1 block" style={{ color: theme.textSecondary }}>
              {calendarSummary.paidCount} paid this cycle
            </span>
          </div>

          <div
            className="p-4 rounded-2xl border transition-all"
            style={{
              backgroundColor: calendarSummary.overdueBills.length > 0
                ? (isDark ? '#3B1818' : '#FEF2F2')
                : theme.cardBackground,
              borderColor: calendarSummary.overdueBills.length > 0
                ? '#EF4444'
                : (isDark ? '#3A3A3C' : '#E5E5EA'),
            }}
          >
            <div className="flex items-center justify-between">
              <span
                className="text-xs font-medium"
                style={{
                  color: calendarSummary.overdueBills.length > 0 ? '#EF4444' : theme.textSecondary,
                }}
              >
                Urgent Attention
              </span>
              <div
                className={`w-7 h-7 rounded-lg flex items-center justify-center ${
                  calendarSummary.overdueBills.length > 0 ? 'bg-red-500 text-white animate-pulse' : 'bg-blue-500/15 text-blue-500'
                }`}
              >
                <AlertCircle className="w-4 h-4" />
              </div>
            </div>
            <div
              className={`text-xl md:text-2xl font-bold mt-2 ${
                calendarSummary.overdueBills.length > 0
                  ? 'text-red-600 dark:text-red-400'
                  : ''
              }`}
              style={{
                color: calendarSummary.overdueBills.length > 0 ? undefined : theme.text,
              }}
            >
              {calendarSummary.overdueBills.length > 0
                ? `${calendarSummary.overdueBills.length} Overdue`
                : calendarSummary.dueSoonBills.length > 0
                ? `${calendarSummary.dueSoonBills.length} Due Soon`
                : 'All Good!'}
            </div>
            <span className="text-xs mt-1 block" style={{ color: theme.textSecondary }}>
              {calendarSummary.urgentReminders.length > 0
                ? `${calendarSummary.urgentReminders.length} actionable alert${calendarSummary.urgentReminders.length !== 1 ? 's' : ''}`
                : 'No immediate deadlines'}
            </span>
          </div>
        </div>

        {/* Urgent Due-Date Reminders Banner */}
        <BillsDueRemindersBanner
          urgentBills={calendarSummary.urgentReminders}
          onPayBill={(bill) => {
            setPayingBill(bill);
            setShowPayDialog(true);
          }}
          formatCurrency={formatCurrency}
          theme={theme}
          isDark={isDark}
        />

        {/* View Switch: Calendar vs List */}
        {viewMode === 'calendar' ? (
          <BillsCalendarView
            bills={bills}
            currentMonth={currentCalendarMonth}
            onMonthChange={setCurrentCalendarMonth}
            onOpenAddDialog={handleOpenAddDialog}
            onOpenEditDialog={handleOpenEditDialog}
            onMarkPaid={(bill) => {
              setPayingBill(bill);
              setShowPayDialog(true);
            }}
            onDeleteBill={(bill) => {
              setDeletingBill(bill);
              setShowDeleteDialog(true);
            }}
            formatCurrency={formatCurrency}
            theme={theme}
            isDark={isDark}
          />
        ) : (
          <div>
            {/* Filter Tabs for List View */}
            <div className="flex gap-2 mb-4">
              {['all', 'upcoming', 'paid'].map(filter => (
                <button
                  key={filter}
                  onClick={() => setActiveFilter(filter)}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-all"
                  style={{
                    backgroundColor: activeFilter === filter 
                      ? theme.accent 
                      : (isDark ? '#2C2C2E' : '#F8F9FA'),
                    color: activeFilter === filter ? 'white' : theme.textSecondary
                  }}
                >
                  {filter.charAt(0).toUpperCase() + filter.slice(1)}
                </button>
              ))}
            </div>

            {/* Bills List */}
            {loading ? (
              <div className="text-center py-12">
                <p style={{ color: theme.textSecondary }}>Loading bills...</p>
              </div>
            ) : filteredBills.length === 0 ? (
              <div 
                className="text-center py-16 rounded-xl"
                style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
              >
                <div 
                  className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
                  style={{ backgroundColor: isDark ? '#2C2C2E' : '#F8F9FA' }}
                >
                  <Calendar className="w-8 h-8" style={{ color: theme.accent }} />
                </div>
                <h3 className="text-lg font-semibold mb-2" style={{ color: theme.text }}>
                  {activeFilter === 'all' ? 'No bills yet' : `No ${activeFilter} bills`}
                </h3>
                <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
                  {activeFilter === 'all' ? 'Tap + to add your first bill' : 'Check other filters'}
                </p>
                {activeFilter === 'all' && (
                  <Button onClick={() => handleOpenAddDialog()} style={{ backgroundColor: theme.accent, color: 'white' }}>
                    Add Bill
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-3">
            {filteredBills.map(bill => {
              const computedStatus = getBillStatus(bill);
              const isPaidTab = activeFilter === 'paid';
              const displayStatus = isPaidTab && (computedStatus !== 'paid' || bill.lastPaidDate)
                ? 'paid'
                : computedStatus;
              const statusBadge = getStatusBadge(bill, displayStatus);

              return (
                <div
                  key={bill.id}
                  className="rounded-xl p-4 transition-all hover:shadow-lg"
                  style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
                  data-testid={`bill-card-${bill.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center"
                        style={{ backgroundColor: isDark ? '#3A3A3C' : '#F8F9FA' }}
                      >
                        {displayStatus === 'overdue' ? (
                          <AlertCircle className="w-6 h-6 text-red-500" />
                        ) : displayStatus === 'paid' ? (
                          <Check className="w-6 h-6 text-green-500" />
                        ) : (
                          <Bell className="w-6 h-6" style={{ color: theme.textSecondary }} />
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold" style={{ color: theme.text }}>{bill.name}</h3>
                        <p className="text-sm" style={{ color: theme.textSecondary }}>
                          {activeFilter === 'paid'
                            ? `Paid on ${format(bill.lastPaidDate || bill.dueDate, 'MMM d, yyyy, p')}`
                            : `${format(bill.dueDate, 'MMM d, yyyy')} • ${getDaysUntilDue(bill.dueDate)}`
                          }
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <div className="text-right">
                        <p className="font-bold text-lg" style={{ color: theme.text }}>
                          {formatCurrency(bill.amount)}
                        </p>
                        <span 
                          className="text-xs px-2 py-1 rounded-full font-medium"
                          style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                        >
                          {statusBadge.text}
                        </span>
                      </div>

                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-lg hover:bg-opacity-10 hover:bg-gray-500">
                            <MoreVertical className="w-5 h-5" style={{ color: theme.textSecondary }} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent style={{ backgroundColor: theme.cardBackground }}>
                          {!isPaidTab && computedStatus !== 'paid' && (
                            <DropdownMenuItem 
                              onClick={() => { setPayingBill(bill); setShowPayDialog(true); }}
                              style={{ color: '#4CAF50' }}
                            >
                              <Check className="w-4 h-4 mr-2" /> Mark as Paid
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem onClick={() => handleOpenEditDialog(bill)} style={{ color: theme.text }}>
                            <Edit2 className="w-4 h-4 mr-2" /> Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => { setDeletingBill(bill); setShowDeleteDialog(true); }}
                            style={{ color: theme.accent }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Quick Pay Button for unpaid bills */}
                  {!isPaidTab && computedStatus !== 'paid' && (
                    <Button
                      onClick={() => { setPayingBill(bill); setShowPayDialog(true); }}
                      className="w-full mt-3"
                      variant="outline"
                      style={{ borderColor: '#4CAF50', color: '#4CAF50' }}
                    >
                      <Check className="w-4 h-4 mr-2" />
                      Mark as Paid
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    )}

        {/* Add/Edit Bill Dialog */}
        <Dialog open={showBillDialog} onOpenChange={setShowBillDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>
                {editingBill ? 'Edit Bill' : 'Add Bill'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label style={{ color: theme.textSecondary }}>Bill Name</Label>
                <Input
                  value={billForm.name}
                  onChange={(e) => setBillForm({ ...billForm, name: e.target.value })}
                  placeholder="e.g., Electricity, Netflix"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Amount ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={billForm.amount}
                  onChange={(e) => setBillForm({ ...billForm, amount: e.target.value })}
                  placeholder="2500"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Due Date</Label>
                <Input
                  type="date"
                  value={billForm.dueDate}
                  onChange={(e) => setBillForm({ ...billForm, dueDate: e.target.value })}
                  className="mt-1"
                  style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Repeat</Label>
                <Select value={billForm.repeat} onValueChange={(v) => setBillForm({ ...billForm, repeat: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None (One-time)</SelectItem>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="yearly">Yearly</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Reminder (days before)</Label>
                <Select value={billForm.reminderDays} onValueChange={(v) => setBillForm({ ...billForm, reminderDays: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 day before</SelectItem>
                    <SelectItem value="3">3 days before</SelectItem>
                    <SelectItem value="7">1 week before</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Account to Debit (Optional)</Label>
                <Select value={billForm.account} onValueChange={(v) => setBillForm({ ...billForm, account: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}>
                    <SelectValue placeholder="Select account" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {accounts.map(acc => (
                      <SelectItem key={acc.id} value={acc.name}>{acc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBillDialog(false)} style={{ color: theme.text }}>
                Cancel
              </Button>
              <Button onClick={handleSaveBill} style={{ backgroundColor: theme.accent, color: 'white' }}>
                {editingBill ? 'Update' : 'Add'} Bill
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Paid Dialog */}
        <AlertDialog open={showPayDialog} onOpenChange={setShowPayDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Mark Bill as Paid</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                {payingBill?.account 
                  ? `Would you like to also log an expense transaction for ${formatCurrency(payingBill?.amount || 0)} from ${payingBill?.account}?`
                  : `Mark "${payingBill?.name}" as paid?`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              {payingBill?.account && (
                <Button variant="outline" onClick={() => handleMarkPaid(false)}>
                  Just Mark Paid
                </Button>
              )}
              <AlertDialogAction 
                onClick={() => handleMarkPaid(!!payingBill?.account)} 
                style={{ backgroundColor: '#4CAF50' }}
              >
                {payingBill?.account ? 'Pay & Log Transaction' : 'Mark as Paid'}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Delete Bill</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to delete "{deletingBill?.name}"? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteBill} style={{ backgroundColor: '#DC3545' }}>
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default Bills;
